/** @jsx jsx */
import React, { Fragment, useState } from 'react'
import { observer } from 'mobx-react'
import { jsx } from '@emotion/core'
import { AdvButton } from '@components/Button'
import { AccountTransferInfoWrap, CreateAccountBtn, LaterBtn } from '../index.style'
import { GlobalStoreKey } from '@v2/constants/common'
import useRootStore from '@store/useRootStore'
import { Checkbox, Radio } from 'antd'
import DialogUtils from '@utils/dialog-utils'
import LightBulbIco from '@shared/Icon/EtcIcon/LightBulbIco'
import { ACCOUNT_TYPE, LogoutType } from '@interface/common'
import * as Utils from '@utils/utils'
import { getAccountTypeText } from '@utils/utils'
import { useOnMount } from '@utils/reactExt'
import { ACCOUNT_STATE } from 'adv-protocol/src/GameCommon'
import * as _ from 'lodash'
import { AuthInfo } from '@store/authStore'
import StorageUtils from '@utils/storage-utils'
import dayjs from 'dayjs'
import { TransferableAccount } from '@store/userStore'
import { isMobile } from 'react-device-detect'


export const AccountTransferInfo: React.FC = observer(() => {
  const { authStore, globalStore, joinStore, userStore, routerStore } = useRootStore()
  const { accountTransferPopupCallback } = globalStore
  const [sameNameAgree, setSameNameAgree] = useState(false)
  const [selectionAgree, setSelectionAgree] = useState(false)
  const [impossibilityAgree, setImpossibilityAgree] = useState(false)
  const [authInfo, setAuthInfo] = useState<AuthInfo>(null)
  const [accountList, setAccountList] = useState<TransferableAccount[]>([])
  const [selectAccount, setSelectAccount] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  // Img
  const imageUrl = globalStore.findGlobalSetting(GlobalStoreKey.INGAME_IMAGES_URL).value
  const createAccountBtnImgUrl = `${imageUrl}guide/account_transfer/create_account_btn.png`
  const applicationBtnImgUrl = `${imageUrl}guide/account_transfer/account_transfer_btn.png`
  const applicationActiveBtnImgUrl = `${imageUrl}guide/account_transfer/account_transfer_btn_active.png`
  const inquiryBtnImgUrl = `${imageUrl}guide/account_transfer/inquiry_btn.png`
  const createAccountBtnImg = <img className="btn-txt" src={createAccountBtnImgUrl} alt="새로운 계정 생성하기"/>
  const applicationBtnImg = <img className="btn-txt" src={applicationBtnImgUrl} alt="이관 신청하기 비활성 상태"/>
  const applicationActiveBtnImg = <img className="btn-txt" src={applicationActiveBtnImgUrl} alt="이관 신청하기 활성 상태"/>
  const inquiryBtnImg = <img className="btn-txt" src={inquiryBtnImgUrl} alt="1:1 문의하기"/>

  const accountTransferOpt = globalStore.getAccountTransferOptions()

  let fromYear: number
  let fromMonth: number
  let fromDate: number
  let fromDayOfWeek: string

  if (!_.isEmpty(accountTransferOpt?.period?.from)) {
    const from = accountTransferOpt.period.from
    const periodFrom = dayjs(from)
    fromYear = periodFrom.year()
    fromMonth = periodFrom.month() + 1
    fromDate = periodFrom.date()
    fromDayOfWeek = Utils.getDayOfWeeks(from)
  }

  let endYear: number
  let endMonth: number
  let endDate: number
  let endDayOfWeek: string

  if (!_.isEmpty(accountTransferOpt?.period?.to)) {
    const to = accountTransferOpt.period.to
    const periodEnd = dayjs(to)
    endYear = periodEnd.year()
    endMonth = periodEnd.month() + 1
    endDate = periodEnd.date()
    endDayOfWeek = Utils.getDayOfWeeks(to)
  }

  const onClickCreateAccountBtn = () => {
    globalStore.setVisibleSideBar(false)
    globalStore.setVisibleAccountTransferPopup(false)
    joinStore.setAccountType(ACCOUNT_TYPE.LOCAL)
    authStore.logout(LogoutType.USER, false).catch(console.error)
    routerStore.push(`/agree?alternativeLoginType=kakao`)
  }

  const onCloseModal = () => {
    StorageUtils.saveAccountTransferInfoHideTime()
    globalStore.setVisibleAccountTransferPopup(false)

    if (accountTransferPopupCallback) {
      accountTransferPopupCallback().catch(console.error)
      globalStore.setAccountTransferPopupCallback(null)
    }
  }

  const onClickApplicationBtn = () => {
    if (isLoading) {
      return
    }

    if (_.isEmpty(selectAccount)) {
      return
    }

    if (!sameNameAgree || !impossibilityAgree || !selectionAgree) {
      return
    }

    setIsLoading(true)
    DialogUtils.showApplyForAccountTransferInfo(
      selectAccount,
      () => { onCloseModal() },
      () => { setIsLoading(false) }
    )
  }

  const onClickTransferBtn = (e) => {
    e.preventDefault()
    setSelectAccount(e.target.value)
  }

  const onchangeSameNameAgree = () => {
    if (sameNameAgree === false) {
      setSameNameAgree(true)
    } else {
      setSameNameAgree(false)
    }
  }
  const onchangeSelectionAgree = () => {
    if (selectionAgree === false) {
      setSelectionAgree(true)
    } else {
      setSelectionAgree(false)
    }
  }
  const onchangeImpossibilityAgree = () => {
    if (impossibilityAgree === false) {
      setImpossibilityAgree(true)
    } else {
      setImpossibilityAgree(false)
    }
  }

  const makeBlockReasonText = (account: TransferableAccount): string => {
    let reason = ''

    const { isPlayBlock, blockDate } = account

    if (isPlayBlock) {
      reason = '이용제한'
    } else if (blockDate < 0) {
      reason = '영구정지'
    } else {
      reason = `${blockDate}일 정지`
    }

    return reason
  }

  const isAgreeAll = !_.isEmpty(selectAccount) && sameNameAgree && selectionAgree && impossibilityAgree

  let contents
  if (!isLoading && !_.isEmpty(accountList)) {
    const isAllBlock = _.findIndex(accountList, { state: ACCOUNT_STATE.REGISTERED, isPlayBlock: false }) === -1

    let subContent: JSX.Element
    if (isAllBlock) {
      subContent = (
        <div className="bottom-notice no-bg">
          <p className="point-color">이관 가능한 계정이 없습니다.</p>
          <AdvButton
            contents={inquiryBtnImg}
            onClick={() => {
              const inquiryHomepageUrl = `${isMobile ? process.env.HOMEPAGE_MO : process.env.HOMEPAGE_PC}/#/auth_login/${authInfo?.authKey}/inquiry?inquiryType=9`
              globalStore.toMoveWebHomePage(inquiryHomepageUrl, 'popup')
            }}
            css={CreateAccountBtn}
            className="application-btn active"
          />
          <AdvButton
            contents="나중에 하기"
            onClick={onCloseModal}
            css={LaterBtn}
          />
        </div>
      )
    } else {
      const btnContents = isAgreeAll ? applicationActiveBtnImg : applicationBtnImg

      subContent = (
        <div className="bottom-notice">
          <h2 className="notice-title check-list">필독! 체크사항</h2>
          <ul className="transfer-contents-list">
            <li className="transfer-contents-list--item">
              <Checkbox className="agree-check-box first-list" defaultChecked={sameNameAgree} checked={sameNameAgree}
                        onChange={onchangeSameNameAgree}>
                동일명의 계정으로만 계정 이관 신청이 가능합니다.
                <span className="bold">선택한 계정의 모든 정보는 카카오 계정의 게임 이용 정보만 남도록 변경됩니다.</span>
                내역이 교체되어도 무관한 계정을 선택하시길 바랍니다.
                <span className="point-color">(필수)</span>
              </Checkbox>
            </li>
            <li className="transfer-contents-list--item">
              <Checkbox className="agree-check-box" defaultChecked={selectionAgree} checked={selectionAgree}
                        onChange={onchangeSelectionAgree}>
                이관 완료 이후부터는 <span className="bold">선택한 계정으로 접속 시</span> 카카오 게임데이터 그대로 승부사온라인을 이용할 수 있습니다.
                <span className="point-color">(필수)</span>
              </Checkbox>
            </li>
            <li className="transfer-contents-list--item">
              <Checkbox className="agree-check-box" defaultChecked={impossibilityAgree} checked={impossibilityAgree}
                        onChange={onchangeImpossibilityAgree}>
                <span className="bold">이관 신청은 취소 및 재신청 할 수 없으며</span>, 이관 완료 후 원복할 수 없습니다.
                <span className="point-color">(필수)</span>
              </Checkbox>
            </li>
          </ul>
          <AdvButton
            contents={btnContents}
            onClick={onClickApplicationBtn}
            className={`application-btn${isAgreeAll ? ' active' : ''}`}
            css={CreateAccountBtn}
          />
          <AdvButton
            contents="나중에 하기"
            onClick={onCloseModal}
            css={LaterBtn}
          />
        </div>
      )
    }

    contents = (
      <Fragment>
        <p className="account-transfer-txt">재화 및 정보가 소실되어도 무관한 계정을 선택하세요.</p>
        <Radio.Group buttonStyle="solid" onChange={onClickTransferBtn}
                     value={selectAccount}>
          {accountList.map((account) => {
            const disabled = account.isPlayBlock || account.state === ACCOUNT_STATE.BLOCKED
            return (
              <Radio key={account.sign} value={account} disabled={disabled}
                     className={_.isEmpty(account.accountName) ? 'adv-hidden' : 'account-transfer-list'}>
                <p className="account-name">{getAccountTypeText(account.sign)}</p>
                <p className="account-address">{account.accountName}</p>
                {!disabled && (<p className="availability">가능</p>)}
                {disabled && (
                  <p className="availability">
                    불가<span className="reason">{makeBlockReasonText(account)}</span>
                  </p>
                )}
              </Radio>
            )
          })}
        </Radio.Group>
        { subContent }
      </Fragment>
    )
  } else if (!isLoading && _.isEmpty(accountList)) {
    contents = (
      <Fragment>
        <div className="account-transfer-list impossible">
          <span className="point-color">카카오 계정 외 이관할 수 있는 계정이 없습니다.</span>
        </div>
        <p className="impossible-Instruction">
          새로운 계정을 생성하여 이관 신청해 주시기 바랍니다.<br/>
          멤버십/구글/네이버/애플 가입이 가능합니다.
        </p>
        <AdvButton
          contents={createAccountBtnImg}
          onClick={onClickCreateAccountBtn}
          css={CreateAccountBtn}
        />
        <AdvButton
          contents="나중에 하기"
          onClick={onCloseModal}
          css={LaterBtn}
        />
        <div className="bottom-notice">
          <h2 className="notice-title">
            <LightBulbIco/>
            유의사항 꼭 읽어 주세요!
          </h2>
          <ul>
            <li className="bottom-notice-list">
              <span className="bullet"/>동일 명의 계정으로만 계정 이관 신청이 가능합니다.
            </li>
            <li className="bottom-notice-list">
              <span className="bullet"/>본인이 선택한 계정의 재화 및 베팅 내역, 활동 점수, 나도 승부사 등 모든 게임 이용 정보는 카카오 계정의 게임 이용정보로 변경됩니다.
            </li>
            <li className="bottom-notice-list">
              <span className="bullet"/>본인이 설정한 이용 제한 상태 혹은 제재 상태일 경우 이관이 불가능합니다.
            </li>
            <li className="bottom-notice-list">
              <span className="bullet"/>이관 신청은 취소할 수 없으며, 이관 완료 후 원복 할 수 없습니다.
            </li>
          </ul>
        </div>
      </Fragment>
    )
  } else {
    contents = (<Fragment/>)
  }

  useOnMount(async () => {
    setIsLoading(true)
    const result = await userStore.getTransferableAccountInfo()
    const { authInfo, accounts } = result

    setIsLoading(false)
    setAuthInfo(authInfo)
    setAccountList(accounts)
  })

  return (
    <AccountTransferInfoWrap>
      <h1 className="kakao-notice-title">
        카카오 서비스 종료<br/>계정 이관 신청 안내
      </h1>
      <div className="notice-summary">
        <p className="notice-summary--text">
          <strong>
            <span className="point-color">
            최근 카카오의 세부 운영정책 변경으로 인해 &quot;카카오의 직접제휴
          (카카오계열)가 아닌 게임서비스는카카오 로그인 서비스를 이용할수 없게 되었다&quot;
            </span>
          </strong>
          는 방침이 적용되어 기존 제공되던
          <strong>
            {`승부사온라인의 간편 [카카오 로그인] 서비스가 부득이하게 ${endYear}년 ${endMonth}월 ${endDate}일 종료될 예정입니다.`}
          </strong>
          {`(신규가입은 ${fromMonth}/${fromDate}일 ${fromDayOfWeek}요일 정기점검 이후 불가)`}
        </p>
        <p className="notice-summary--text detail">
          이는 기타 게임사에 공통 적용되는 사안으로
          본의 아니게 베터님께 불편을 끼쳐드린 점 양해부탁드립니다.
        </p>
        <p className="notice-summary--text detail">
          이에 따라 승부사온라인은 기존 카카오 계정을
          사용하시는 베터님께 최대한 피해가 없도록
          카카오가 아닌 동일명의의 다른 추가계정(구글,네이버,애플,멤버십)을
          지정하시어 카카오 계정에서 사용하던 재화를 포함한
          {`모든 데이터를 ${endMonth}/${endDate}일까지 이관되도록 할 예정입니다.`}
          (지정된 ID에는 이관된 카카오의 정보 데이터만 남습니다.)
        </p>
        <p className="notice-summary--text detail">
          자세한 사항은 아래 내용을 참고해 주시길 바라며,
          베터님께 불편을 끼치게된 점 다시 한번 진심으로 사과말씀드립니다.
          게임이용에 차질이 없도록 만전을 기하도록 하겠습니다.
        </p>
      </div>
      <div className="article">
        <h2 className="article-tit">
          이관 신청 기간
        </h2>
        <p className="article-txt">
          {`${fromYear}.${fromMonth}.${fromDate}일(${fromDayOfWeek}) 정기점검 후 ~ ${endYear}.${endMonth}.${endDate}일(${endDayOfWeek}) 정기점검 전`}<br/>
          ※반드시 기간내에 이관신청 해주세요.
        </p>
        <h2 className="article-tit">
          이관 실행 일자
        </h2>
        <p className="article-txt">
          {`${endYear}.${endMonth}.${endDate}일(${endDayOfWeek}) 정기점검`}
        </p>
        <h2 className="article-tit">
          이관 가능 계정
        </h2>
      </div>
      { contents }
    </AccountTransferInfoWrap>
  )
})
